/**
 * ============================================================================
 * UTILITY HELPERS
 * ============================================================================
 *
 * Common utility functions used throughout the application.
 *
 * ============================================================================
 */

/**
 * DOM ready function
 * @param {Function} callback - Function to execute when DOM is ready
 */
function ready(callback) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback);
  } else {
    callback();
  }
}

/**
 * Debounce function
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @param {boolean} immediate - Execute immediately
 * @returns {Function} Debounced function
 */
function debounce(func, wait, immediate = false) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      timeout = null;
      if (!immediate) func(...args);
    };
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
    if (callNow) func(...args);
  };
}

/**
 * Throttle function
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit in milliseconds
 * @returns {Function} Throttled function
 */
function throttle(func, limit) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

/**
 * Get element by selector with optional context
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (default: document)
 * @returns {Element|null} Found element
 */
function $(selector, context = document) {
  return context.querySelector(selector);
}

/**
 * Get elements by selector with optional context
 * @param {string} selector - CSS selector
 * @param {Element} context - Context element (default: document)
 * @returns {NodeList} Found elements
 */
function $$(selector, context = document) {
  return context.querySelectorAll(selector);
}

/**
 * Add event listener with optional delegation
 * @param {Element|string} target - Target element or selector
 * @param {string} event - Event type
 * @param {Function} handler - Event handler
 * @param {string} delegate - Delegation selector
 */
function on(target, event, handler, delegate) {
  const element = typeof target === 'string' ? $(target) : target;

  if (!element) return;

  if (delegate) {
    element.addEventListener(event, (e) => {
      if (e.target.matches(delegate)) {
        handler.call(e.target, e);
      }
    });
  } else {
    element.addEventListener(event, handler);
  }
}

/**
 * Remove event listener
 * @param {Element|string} target - Target element or selector
 * @param {string} event - Event type
 * @param {Function} handler - Event handler
 */
function off(target, event, handler) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.removeEventListener(event, handler);
  }
}

/**
 * Check if element matches selector
 * @param {Element} element - Element to check
 * @param {string} selector - CSS selector
 * @returns {boolean} True if matches
 */
function matches(element, selector) {
  return element.matches(selector);
}

/**
 * Get closest ancestor matching selector
 * @param {Element} element - Starting element
 * @param {string} selector - CSS selector
 * @returns {Element|null} Closest matching ancestor
 */
function closest(element, selector) {
  return element.closest(selector);
}

/**
 * Add class to element
 * @param {Element|string} target - Target element or selector
 * @param {string} className - Class name to add
 */
function addClass(target, className) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.classList.add(className);
  }
}

/**
 * Remove class from element
 * @param {Element|string} target - Target element or selector
 * @param {string} className - Class name to remove
 */
function removeClass(target, className) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.classList.remove(className);
  }
}

/**
 * Toggle class on element
 * @param {Element|string} target - Target element or selector
 * @param {string} className - Class name to toggle
 * @returns {boolean} True if class was added
 */
function toggleClass(target, className) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    return element.classList.toggle(className);
  }
  return false;
}

/**
 * Check if element has class
 * @param {Element|string} target - Target element or selector
 * @param {string} className - Class name to check
 * @returns {boolean} True if has class
 */
function hasClass(target, className) {
  const element = typeof target === 'string' ? $(target) : target;
  return element ? element.classList.contains(className) : false;
}

/**
 * Get/set element attribute
 * @param {Element|string} target - Target element or selector
 * @param {string} name - Attribute name
 * @param {string} value - Attribute value (optional)
 * @returns {string|null} Attribute value if getting
 */
function attr(target, name, value) {
  const element = typeof target === 'string' ? $(target) : target;
  if (!element) return null;

  if (value !== undefined) {
    element.setAttribute(name, value);
  } else {
    return element.getAttribute(name);
  }
}

/**
 * Remove attribute from element
 * @param {Element|string} target - Target element or selector
 * @param {string} name - Attribute name
 */
function removeAttr(target, name) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.removeAttribute(name);
  }
}

/**
 * Get/set element data attribute
 * @param {Element|string} target - Target element or selector
 * @param {string} name - Data attribute name (without data- prefix)
 * @param {string} value - Data attribute value (optional)
 * @returns {string|null} Data attribute value if getting
 */
function data(target, name, value) {
  return attr(target, `data-${name}`, value);
}

/**
 * Create element with optional attributes and content
 * @param {string} tag - HTML tag name
 * @param {Object} attributes - Element attributes
 * @param {string|Element} content - Element content
 * @returns {Element} Created element
 */
function createElement(tag, attributes = {}, content = '') {
  const element = document.createElement(tag);

  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });

  if (typeof content === 'string') {
    element.textContent = content;
  } else if (content instanceof Element) {
    element.appendChild(content);
  }

  return element;
}

/**
 * Check if device is mobile
 * @returns {boolean} True if mobile
 */
function isMobile() {
  return window.innerWidth < 768;
}

/**
 * Check if device is tablet
 * @returns {boolean} True if tablet
 */
function isTablet() {
  return window.innerWidth >= 768 && window.innerWidth < 1024;
}

/**
 * Check if device is desktop
 * @returns {boolean} True if desktop
 */
function isDesktop() {
  return window.innerWidth >= 1024;
}

/**
 * Get viewport dimensions
 * @returns {Object} Viewport width and height
 */
function getViewport() {
  return {
    width: window.innerWidth,
    height: window.innerHeight
  };
}

/**
 * Scroll to element smoothly
 * @param {Element|string} target - Target element or selector
 * @param {Object} options - Scroll options
 */
function scrollTo(target, options = {}) {
  const element = typeof target === 'string' ? $(target) : target;
  if (element) {
    element.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      ...options
    });
  }
}

/**
 * Format money using Shopify's money format
 * @param {number} cents - Amount in cents
 * @param {string} format - Money format string
 * @returns {string} Formatted money string
 */
function formatMoney(cents, format = '${{amount}}') {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const value = (cents / 100).toFixed(2);

  return format
    .replace('{{amount}}', value)
    .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
    .replace('{{amount_with_comma_separator}}', value.replace('.', ','));
}
